import type { Archive } from '../types';

/** A heartbeat older than this means the subprocess is presumed dead.
 *  Indexer pings every batch (~200 snapshots), so even on slow R2
 *  reads a healthy run hits it within seconds. */
const STALE_AFTER_MS = 3 * 60 * 1000;

export type IndexerState =
  | { state: 'idle' }
  | { state: 'running'; startedAt: string }
  | { state: 'stalled'; startedAt: string; heartbeatAt: string | null; staleForMs: number };

/** Classify what the indexer is doing for this archive based on
 *  started_at and heartbeat. Treats "no heartbeat ever but started very
 *  recently" as still running — it might just not have completed its
 *  first batch yet. */
export function indexerState(archive: Pick<Archive, 'text_indexing_started_at' | 'text_indexing_heartbeat_at'>): IndexerState {
  const startedAt = archive.text_indexing_started_at;
  if (!startedAt) return { state: 'idle' };

  const heartbeatAt = archive.text_indexing_heartbeat_at ?? null;
  const now = Date.now();
  const lastSeen = heartbeatAt ? Date.parse(heartbeatAt) : Date.parse(startedAt);
  const staleForMs = now - lastSeen;

  if (staleForMs > STALE_AFTER_MS) {
    return { state: 'stalled', startedAt, heartbeatAt, staleForMs };
  }
  return { state: 'running', startedAt };
}

/** "no activity for 4 min" / "1h 12m" — human-readable elapsed time. */
export function fmtElapsed(ms: number): string {
  const sec = Math.floor(ms / 1000);
  if (sec < 90) return `${sec} sec`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem === 0 ? `${hr}h` : `${hr}h ${rem}m`;
}
